import { cn } from "@/lib/utils";

// Phase 4i catalog-refinement — silhouette fallback for the
// "category card has no photo" case. Replaces the generic "JDR"
// wordmark that the 2026-05-25 design audit flagged on every empty
// tile. Each silhouette is a simple geometric line-drawing on the
// flag-navy brand background, with the category name underneath so
// the card is never anonymous.
//
// New categories the operator adds after launch fall through to the
// JDR wordmark — we don't want to ship a "shape coming soon"
// placeholder for the placeholder.

export interface EquipmentSilhouetteProps {
  categorySlug: string | null | undefined;
  className?: string;
}

interface SilhouetteEntry {
  label: string;
  // Children rendered inside a <svg viewBox="0 0 96 64"> so every
  // shape stays in the same proportional space. White stroke / fill
  // on the navy gradient background.
  draw: JSX.Element;
}

const SILHOUETTES: Record<string, SilhouetteEntry> = {
  "skid-steers": {
    label: "Skid steer",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Cab body */}
        <path d="M28 40 L28 26 L40 22 L56 22 L60 26 L60 40 Z" />
        {/* Lift arm */}
        <path d="M58 24 L78 30 L78 40" />
        {/* Bucket */}
        <path d="M74 38 L86 38 L84 44 L74 44 Z" />
        {/* Tracks */}
        <rect x="24" y="42" width="44" height="8" rx="3" />
        <circle cx="32" cy="46" r="2.2" fill="currentColor" />
        <circle cx="46" cy="46" r="2.2" fill="currentColor" />
        <circle cx="60" cy="46" r="2.2" fill="currentColor" />
      </g>
    )
  },

  excavators: {
    label: "Excavator",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Cab */}
        <path d="M22 36 L22 24 L34 20 L42 20 L46 24 L46 36 Z" />
        {/* Boom */}
        <path d="M44 28 L58 18 L66 26" />
        {/* Stick */}
        <path d="M66 26 L78 36" />
        {/* Bucket */}
        <path d="M76 34 L84 34 L86 42 L78 44 Z" />
        {/* Tracks */}
        <rect x="14" y="42" width="50" height="9" rx="3" />
        <circle cx="22" cy="46.5" r="2.4" fill="currentColor" />
        <circle cx="39" cy="46.5" r="2.4" fill="currentColor" />
        <circle cx="56" cy="46.5" r="2.4" fill="currentColor" />
      </g>
    )
  },

  "mini-excavators": {
    label: "Mini excavator",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Smaller cab */}
        <path d="M30 38 L30 28 L40 25 L46 25 L50 28 L50 38 Z" />
        {/* Short boom */}
        <path d="M49 30 L58 24 L64 30" />
        {/* Short stick */}
        <path d="M64 30 L72 38" />
        {/* Small bucket */}
        <path d="M70 36 L78 36 L80 42 L72 44 Z" />
        {/* Compact tracks */}
        <rect x="26" y="42" width="32" height="8" rx="3" />
        <circle cx="32" cy="46" r="2.2" fill="currentColor" />
        <circle cx="42" cy="46" r="2.2" fill="currentColor" />
        <circle cx="52" cy="46" r="2.2" fill="currentColor" />
      </g>
    )
  },

  "aerial-lifts": {
    label: "Aerial lift",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Platform basket */}
        <path d="M60 12 L84 12 L84 20 L60 20 Z" />
        {/* Operator rail */}
        <path d="M62 12 L62 8 L82 8 L82 12" />
        {/* Telescoping boom (two segments) */}
        <path d="M36 38 L58 22" />
        <path d="M48 30 L62 20" />
        {/* Truck/base cab */}
        <path d="M18 38 L18 32 L34 32 L34 38" />
        <path d="M34 38 L34 42 L18 42 Z" />
        {/* Wheels */}
        <circle cx="22" cy="46" r="3" fill="currentColor" />
        <circle cx="32" cy="46" r="3" fill="currentColor" />
      </g>
    )
  },

  generators: {
    label: "Generator",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Towable trailer box */}
        <path d="M14 36 L14 22 L82 22 L82 36 Z" />
        {/* Top vents */}
        <path d="M22 22 L22 18 L36 18 L36 22" />
        <path d="M44 22 L44 18 L58 18 L58 22" />
        {/* Control panel */}
        <rect x="62" y="26" width="14" height="6" rx="1" />
        <circle cx="66" cy="29" r="1.2" fill="currentColor" />
        <circle cx="71" cy="29" r="1.2" fill="currentColor" />
        {/* Trailer tongue */}
        <path d="M14 30 L6 36" />
        {/* Wheels */}
        <circle cx="32" cy="40" r="4" />
        <circle cx="32" cy="40" r="1.4" fill="currentColor" />
        <circle cx="64" cy="40" r="4" />
        <circle cx="64" cy="40" r="1.4" fill="currentColor" />
      </g>
    )
  },

  "portable-generators": {
    label: "Portable generator",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Box */}
        <path d="M22 40 L22 22 L72 22 L72 40 Z" />
        {/* Handle bar */}
        <path d="M22 22 L18 14 L76 14 L72 22" />
        {/* Round outlet */}
        <circle cx="36" cy="32" r="3.5" />
        <circle cx="36" cy="32" r="1.2" fill="currentColor" />
        {/* Control panel */}
        <rect x="50" y="28" width="16" height="8" rx="1" />
        <path d="M53 32 L63 32" />
        {/* Wheels */}
        <circle cx="30" cy="44" r="3" fill="currentColor" />
        <circle cx="64" cy="44" r="3" fill="currentColor" />
      </g>
    )
  },

  compaction: {
    label: "Compaction",
    draw: (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Cab/seat post */}
        <path d="M40 18 L56 18 L60 26 L60 34 L40 34 Z" />
        {/* Operator seat hint */}
        <path d="M48 18 L48 12 L52 12 L52 18" />
        {/* Front drum */}
        <circle cx="30" cy="42" r="8" />
        <circle cx="30" cy="42" r="3" />
        {/* Rear drum */}
        <circle cx="70" cy="42" r="8" />
        <circle cx="70" cy="42" r="3" />
        {/* Frame connector */}
        <path d="M38 38 L62 38" />
        <path d="M38 46 L62 46" />
      </g>
    )
  }
};

export function EquipmentSilhouette({
  categorySlug,
  className
}: EquipmentSilhouetteProps): JSX.Element {
  const entry = categorySlug ? SILHOUETTES[categorySlug] : null;
  const accessibleName = entry
    ? `${entry.label} illustration`
    : "JDR equipment photo coming soon";

  return (
    <div
      role="img"
      aria-label={accessibleName}
      data-testid="equipment-silhouette"
      data-silhouette={entry ? categorySlug : "fallback"}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1.5 bg-hero-flag text-brand-white",
        className
      )}
    >
      {entry ? (
        <>
          <svg
            viewBox="0 0 96 64"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="h-2/3 w-full text-brand-white opacity-90"
            preserveAspectRatio="xMidYMid meet"
          >
            {entry.draw}
          </svg>
          <span className="text-xs font-medium uppercase tracking-wider text-brand-white/80">
            {entry.label}
          </span>
        </>
      ) : (
        <span className="font-serif text-3xl font-bold italic tracking-tight">
          JDR
        </span>
      )}
    </div>
  );
}
